/**
 * Regular expression to validate email addresses.
 *
 * Rules enforced:
 * - No consecutive dots (`..`)
 * - Cannot start or end with a dot
 * - Dot cannot be immediately before `@`
 * - No spaces or invalid characters
 * - Must contain a valid domain and TLD
 *
 * Example of valid email:
 * - user.name@example.com
 *
 * Example of invalid emails:
 * - .user@example.com
 * - user..name@example.com
 * - user.@example.com
 * - user@.example.com
 *
 * * ^                         → Start of string
 *
 * (?!.*\.\.)                → Disallows consecutive dots (`..`) anywhere
 * (?!\.)                    → Disallows starting with a dot
 * (?!.*\.$)                 → Disallows ending with a dot
 * (?!.*\.@)                 → Disallows a dot immediately before `@`
 *
 * [^\s@(),?]+               → Local part:
 *                             - One or more characters
 *                             - No spaces
 *                             - No `@`, `(`, `)`, `,`, `?`
 *
 * @                         → Required `@` symbol
 *
 * [^\s@(),?]+               → Domain name:
 *                             - One or more valid characters
 *                             - Same restrictions as local part
 *
 * \.                        → Required dot before top-level domain (TLD)
 *
 * [^\s@(),?]+               → Top-level domain (e.g. `com`, `io`, `org`)
 *
 * $                         → End of string
 */
const REGEX_EMAIL_VALID: RegExp =
  /^(?!.*\.\.)(?!\.)(?!.*\.$)(?!.*\.@)[^\s@(),?]+@[^\s@(),?.]+\.[^\s@(),?]+$/;
const REGEX_DOUBLE_DOTS: RegExp = /\.{2,}/g;
const REGEX_SPACES: RegExp = /\s+/g;

export const normalizeEmail = (email: string): string =>
  email.replace(REGEX_SPACES, '').replace(REGEX_DOUBLE_DOTS, '.');

export const isValidEmail = (email: string): boolean =>
  REGEX_EMAIL_VALID.test(email);
